import React, { useCallback } from 'react';
import {
	ApolloClientProvider,
	GIRA_APOLLO_CLIENT,
	resetCache,
} from '@atlassian/jira-apollo-multiple-clients';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { usePublishFormContext } from '@atlassian/jira-issue-create-form-context/src/utils';
import { useIssueRestoreMetaService } from '@atlassian/jira-issue-create-restore';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import messages from './common/messages';
import type { Payload } from './common/types';
import { NavigationDiscardChangesContainer } from './controllers/navigation-discard-changes';
import { useIssueCreateFromAnywhereContext } from './controllers/use-issue-create-from-anywhere-context';
import { usePublishUseTriggerIssueCreateModalChange } from './controllers/use-publish-use-trigger-issue-create-modal-change';
import IssueCreateModal from './ui';
import { IssueRestoreWrapper } from './ui/issue-restore-wrapper';
import type { CreateModalProps } from './ui/modal/types';

const onResetApolloCache = () => resetCache(GIRA_APOLLO_CLIENT);

const IssueCreateAppWithApolloProvider = ({ onError }: { onError?: () => void }) => {
	const [{ isModalOpen }] = useTriggerIssueCreateModal();
	const { deleteIssueRestoreMeta } = useIssueRestoreMetaService();

	const _onError = useCallback(() => {
		onError?.();
		deleteIssueRestoreMeta();
	}, [onError, deleteIssueRestoreMeta]);

	const { giraClient } = useIssueCreateFromAnywhereContext();

	usePublishUseTriggerIssueCreateModalChange();

	usePublishFormContext();

	const renderIssueCreateModal = ({
		onTransformCreateFormProps,
		setShouldRestore,
	}: CreateModalProps = {}) => {
		if (isModalOpen) {
			return (
				<IssueCreateModal
					onTransformCreateFormProps={onTransformCreateFormProps}
					setShouldRestore={setShouldRestore}
					onResetApolloCache={onResetApolloCache}
				/>
			);
		}
		return null;
	};

	return (
		<JSErrorBoundary
			id="issue-create-app"
			packageName="jiraIssueCreate"
			fallback="flag"
			onError={_onError}
			sendToPrivacyUnsafeSplunk
		>
			<ApolloClientProvider client={giraClient}>
				<NavigationDiscardChangesContainer>
					<IssueRestoreWrapper>
						{({ onTransformCreateFormProps, setShouldRestore }) => (
							<JSErrorBoundary
								id="issue-create-app-children"
								packageName="jiraIssueCreate"
								fallback="flag"
								onError={_onError}
								sendToPrivacyUnsafeSplunk
							>
								{renderIssueCreateModal({
									onTransformCreateFormProps,
									setShouldRestore,
								})}
							</JSErrorBoundary>
						)}
					</IssueRestoreWrapper>
				</NavigationDiscardChangesContainer>
			</ApolloClientProvider>
		</JSErrorBoundary>
	);
};

export type { Payload };

export { ApolloClientProvider, IssueCreateModal, messages };

export default IssueCreateAppWithApolloProvider;
